import { create } from 'zustand';
// Grid layouts are session-only (client decision 2026-08-05) — reset on log-off so
// each fresh login starts from the factory-default layout. Imported from the leaf
// gridPrefs module (no barrel) to avoid pulling the grid component into the store.
import { clearAllGridPrefs } from '../components/AppDataGrid/gridPrefs';
import type { SectionGrants } from '../layouts/navPermissions';

/**
 * Auth/session state. Token + active division are read by the Axios
 * interceptor (CLAUDE.md Section 7.3 / 7.4).
 *
 * "Keep me signed in": when the user opts in, the session is persisted to
 * localStorage and survives a full reload / browser restart. Otherwise it
 * lives in sessionStorage and dies when the tab closes.
 */
export interface AuthUser {
  id: string;
  /** DISPLAY name — the linked Employee.DisplayName (an E-code, e.g. "E174807"),
   *  for ALPACA parity with legacy's "current user" badge.  A display value:
   *  NEVER use it for an identity check — use `userName`. */
  name: string;
  email: string;
  /** LOGIN username (PermissionPolicyUser.UserName, e.g. "Service") — the same
   *  value the API's IsServiceUser() reads from the JWT "name" claim.  The
   *  service-account gate in navPermissions.canSeeNavItem keys off THIS: on a
   *  database whose Employee row is titled "Service User" rather than "Service",
   *  keying off `name` silently hid every service-only form (Form Config,
   *  Default Settings, App Config, Client Version).  Optional so a session
   *  persisted before this field existed still deserialises. */
  userName?: string;
}

/** Navigation permissions returned by the GoLive XAF RoleNavigationPermission
 *  chain. `isAdmin` short-circuits the gating (admin sees everything). */
export interface NavPermissions {
  isAdmin: boolean;
  roles: string;
  /** XAF NavigationView ids (e.g. "TestMethod_ListView_AnalysisQueue") this
   *  user is allowed to see — from EmployeeRoleNavigationPermission. */
  allowedViews: string[];
  /** Dashboard Oids (lowercase hyphenated GUID strings) the user can see —
   *  from AssignDashboardToUserDepartment.Role CSV intersected with the
   *  user's role Oids.  Backlog 01..07 / ICM dashboards live here, not in
   *  the NavigationItem table. */
  allowedDashboards: string[];
  /** NavigationCaption strings (display labels) the user can see —
   *  collapsed to lowercase, trimmed of whitespace.  Used as a GLOBAL
   *  fallback in canSeeNavItem when an item's NavigationView name doesn't
   *  match any mapping.  Self-healing — new GoLive views with the same
   *  display label as one of our nav items will surface automatically. */
  allowedCaptions: string[];
  /** Global "Form Setup" gate — formId → visible.  Set by an admin on the
   *  Settings → Form Setup page (server-side JSON file).  A form id mapped to
   *  `false` is hidden for EVERYONE (ANDed with the per-user permission above);
   *  ids absent from the map are visible by default. */
  formVisibility?: Record<string, boolean>;
  /** Data Review nav visibility (Default Settings items 6-9 — SDMS Result
   *  Validation / SDMS Result Approval / Result Validation / Result Approval).
   *  Inlined into the login/permissions payload (not a separate app-config
   *  fetch) so the sidebar has the right answer on its very first render —
   *  no loading window to fail open OR closed through (user report
   *  2026-07-17: both directions showed a visible glitch — items flashing
   *  then narrowing, or the whole group appearing late). */
  dataReviewNav?: {
    sdmsResultValidation: boolean;
    sdmsResultApproval: boolean;
    resultValidation: boolean;
    resultApproval: boolean;
    fieldDataValidation: boolean;
    fieldDataApproval: boolean;
    /** SDMS Result Validation form: split category tabs (true) vs single legacy
     *  "Batch Results" tab (false, default). Default Settings → Multi-Tab. */
    multiTab?: boolean;
  };
  /** ALPACA App Config "AVA Enabled" toggle — AVA Insight shows only when true
   *  (parity with the ALPACA header AVA button). Missing → treated as true. */
  avaEnabled?: boolean;
  /** True when the external Help Center URL is configured (dbo.AppConfig.Helpcenterurl)
   *  — the Help Center header link shows only when true. Missing → hidden. */
  helpCenterEnabled?: boolean;
  /** Effective per-form ACTION permissions (Read/Write/Create/Delete), keyed by
   *  lowercased NavigationView.  Derived server-side from the user's assigned
   *  Module Access Control (Role-Based MAC) levels.  Drives which mutating
   *  buttons a form may show (New/Edit/Delete) via `navActionsForForm` /
   *  `useNavActions`.  Empty for admins (they bypass action gating). */
  navActions?: Record<string, { read: boolean; write: boolean; create: boolean; delete: boolean }>;
  /** What the user was granted INSIDE Settings / Data Center (see SectionGrants in navPermissions.ts) — those two sections
   *  show only forms granted there, never forms granted on another module's NavigationItem. */
  sectionGrants?: SectionGrants;
}

interface PersistedAuth {
  token: string;
  user: AuthUser;
  divisionId: string | null;
  divisionCode: string | null;
  permissions: NavPermissions | null;
}

// Namespace the token by the app's base path so a ROOT deploy (prod) and a
// SUB-PATH deploy (e.g. /alpacaintelligence_sandbox/) served from the SAME
// origin keep separate logins in localStorage.  Root build → BASE_URL '/' →
// 'alpaca.auth' (unchanged, so existing prod sessions are preserved).
const KEY = 'alpaca.auth' + (import.meta.env.BASE_URL === '/' ? '' : import.meta.env.BASE_URL.replace(/\/+$/, ''));

/** The ACTIVE DIVISION is deliberately kept in sessionStorage — i.e. PER TAB.
 *  It used to ride along in the shared auth blob, which meant switching
 *  division in one tab silently moved every other tab too: the axios
 *  interceptor sends `X-Division-Id` from this store on every request, so a
 *  sample could be registered against a division the user was no longer
 *  looking at.  Per-tab means one user can legitimately work in two divisions
 *  side by side, and no tab can ever move another tab's scope. */
const DIV_KEY      = KEY + '.division';
const DIV_CODE_KEY = KEY + '.divisionCode';

function readTabDivision(): string | null {
  try { return sessionStorage.getItem(DIV_KEY); } catch { return null; }
}
function writeTabDivision(divisionId: string | null) {
  try {
    if (divisionId) sessionStorage.setItem(DIV_KEY, divisionId);
    else sessionStorage.removeItem(DIV_KEY);
  } catch { /* storage unavailable — stay in-memory only */ }
}
function readTabDivisionCode(): string | null {
  try { return sessionStorage.getItem(DIV_CODE_KEY); } catch { return null; }
}
function writeTabDivisionCode(code: string | null) {
  try {
    if (code) sessionStorage.setItem(DIV_CODE_KEY, code);
    else sessionStorage.removeItem(DIV_CODE_KEY);
  } catch { /* storage unavailable — stay in-memory only */ }
}

/** Normalize a NavigationCaption for case + whitespace insensitive matching. */
function normCaption(s: string): string {
  return s.replace(/\s+/g, ' ').trim().toLowerCase();
}

function readPersisted(): PersistedAuth | null {
  try {
    const raw = localStorage.getItem(KEY) ?? sessionStorage.getItem(KEY);
    if (!raw) return null;
    const data = JSON.parse(raw) as PersistedAuth;
    // Normalize legacy uppercase NavigationView strings to lowercase so the
    // case-insensitive matcher works even for sessions persisted before the
    // normalization fix shipped.
    if (data.permissions) {
      data.permissions = {
        ...data.permissions,
        allowedViews:      (data.permissions.allowedViews      ?? []).map(v => v.toLowerCase()),
        allowedDashboards: (data.permissions.allowedDashboards ?? []).map(d => d.toLowerCase()),
        allowedCaptions:   (data.permissions.allowedCaptions   ?? []).map(normCaption),
      };
    }
    return data;
  } catch {
    return null;
  }
}

function writePersisted(data: PersistedAuth, remember: boolean) {
  try {
    const json = JSON.stringify(data);
    if (remember) {
      localStorage.setItem(KEY, json);
      sessionStorage.removeItem(KEY);
    } else {
      sessionStorage.setItem(KEY, json);
      localStorage.removeItem(KEY);
    }
  } catch { /* storage unavailable — stay in-memory only */ }
}

/** Clear the session (JWT + in-memory state).  Used for BOTH explicit
 *  user logout AND silent token-expiry (401).  Does NOT touch
 *  `alpaca.login.remembered` — that's the user's saved Keep-Me-Signed-In
 *  username/password, and the user only opts out of remembering by
 *  unchecking the box on their next login (which the LoginPage handles).
 *  Wiping it on logout would defeat the checkbox's promise. */
function clearSession() {
  try {
    localStorage.removeItem(KEY);
    sessionStorage.removeItem(KEY);
    sessionStorage.removeItem(DIV_KEY);
    sessionStorage.removeItem(DIV_CODE_KEY);
  } catch { /* ignore */ }
  // Reset grid layouts on log-off / session-expiry (session-only persistence).
  clearAllGridPrefs();
}

interface AuthState {
  token: string | null;
  user: AuthUser | null;
  divisionId: string | null;
  /** Short code for the user's active division (e.g. "MICRO").  Set at login
   *  from the server's LoginResponse and stored per-tab.  Consumed by JobID
   *  generation so no extra API call is needed at submit time. */
  divisionCode: string | null;
  permissions: NavPermissions | null;
  /** Establish a session after a successful login. */
  signIn: (token: string, user: AuthUser, remember: boolean, permissions: NavPermissions | null) => void;
  setToken: (token: string | null) => void;
  setUser: (user: AuthUser | null) => void;
  setDivisionId: (divisionId: string | null) => void;
  /** Set BOTH the division id and code atomically.  Called immediately after
   *  signIn() on every login completion path so both values are available
   *  before any navigation occurs. */
  setDivision: (divisionId: string | null, divisionCode: string | null) => void;
  setPermissions: (permissions: NavPermissions | null) => void;
  /** Explicit user "Sign out" — wipes everything including Keep-Me-Signed-In creds. */
  logout: () => void;
  /** Token-expiry sign-out (called by the 401 axios interceptor).  Drops the JWT
   *  but KEEPS the Keep-Me-Signed-In creds so the login form pre-fills.  Without
   *  this, every 24-hour JWT expiry would force the user to retype username +
   *  password, defeating the whole point of the checkbox. */
  expireSession: () => void;
}

const hydrated = readPersisted();

export const useAuthStore = create<AuthState>((set, get) => ({
  token: hydrated?.token ?? null,
  user: hydrated?.user ?? null,
  divisionId:   readTabDivision()     ?? hydrated?.divisionId   ?? null,   // per-tab wins
  divisionCode: readTabDivisionCode() ?? hydrated?.divisionCode ?? null,   // per-tab wins
  permissions: hydrated?.permissions ?? null,

  signIn: (token, user, remember, permissions) => {
    const normalized = permissions ? {
      ...permissions,
      allowedViews:      permissions.allowedViews.map(v => v.toLowerCase()),
      allowedDashboards: permissions.allowedDashboards.map(d => d.toLowerCase()),
      allowedCaptions:   (permissions.allowedCaptions ?? []).map(normCaption),
    } : null;
    set({ token, user, permissions: normalized });
    writePersisted({ token, user, divisionId: get().divisionId, divisionCode: get().divisionCode, permissions: normalized }, remember);
  },
  setToken: (token) => set({ token }),
  setUser: (user) => set({ user }),
  setDivisionId: (divisionId) => {
    // PER-TAB ONLY (see DIV_KEY): switching division here must never change
    // what another tab is scoped to.
    set({ divisionId });
    writeTabDivision(divisionId);
  },
  setDivision: (divisionId, divisionCode) => {
    // Sets BOTH division id and code atomically — called right after signIn()
    // on every login completion path.  PER-TAB ONLY (same rationale as
    // setDivisionId above).
    set({ divisionId, divisionCode });
    writeTabDivision(divisionId);
    writeTabDivisionCode(divisionCode);
  },
  setPermissions: (permissions) => {
    // Normalize every NavigationView to lowercase so the case differences
    // GoLive returns (e.g. Samplelogin_* vs SampleLogIn_*) don't break matching.
    const normalized = permissions ? {
      ...permissions,
      allowedViews:      permissions.allowedViews.map(v => v.toLowerCase()),
      allowedDashboards: permissions.allowedDashboards.map(d => d.toLowerCase()),
      allowedCaptions:   (permissions.allowedCaptions ?? []).map(normCaption),
    } : null;
    set({ permissions: normalized });
    const { token, user, divisionId, divisionCode } = get();
    if (token && user && localStorage.getItem(KEY)) {
      writePersisted({ token, user, divisionId, divisionCode, permissions: normalized }, true);
    }
  },
  logout: () => {
    // Drops the JWT but keeps the Keep-Me-Signed-In credentials.  The user
    // explicitly asked to be remembered; logout doesn't override that.  To
    // forget the saved creds, the user logs in next time with the checkbox
    // UNCHECKED — the LoginPage submit handler clears them in that case.
    clearSession();
    set({ token: null, user: null, divisionId: null, divisionCode: null, permissions: null });
  },
  expireSession: () => {
    // Identical behaviour to logout (kept as a separate name for telemetry/
    // intent clarity).  Used by the 401 interceptor when the JWT lifetime
    // expires.
    clearSession();
    set({ token: null, user: null, divisionId: null, divisionCode: null, permissions: null });
  },
}));

/* ── Cross-tab session sync ───────────────────────────────────────────────
 * The `storage` event fires in every OTHER tab of the profile when shared
 * localStorage changes.  Two cases matter, and both keep the session flags
 * honest for concurrent-user licensing:
 *
 *   1. SIGNED OUT elsewhere — the auth key is removed.  This tab drops its
 *      session too, so a signed-out user isn't left with a live-looking tab
 *      that keeps heart-beating and holding the licence.
 *   2. A DIFFERENT USER signed in elsewhere — the shared token now belongs to
 *      someone else.  This tab must NOT keep running as the previous user
 *      (its writes would be attributed to them), so it signs out and lands on
 *      the login page.
 *
 * Same user re-authenticating (token refresh) is left alone — that is not an
 * identity change and must not disturb open tabs.
 * ────────────────────────────────────────────────────────────────────────── */
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key !== KEY) return;                       // not the auth slot
    const state = useAuthStore.getState();
    if (!state.token) return;                        // this tab isn't signed in

    if (e.newValue === null) {                       // (1) signed out elsewhere
      state.expireSession();
      return;
    }
    try {                                            // (2) different user elsewhere
      const next = JSON.parse(e.newValue) as PersistedAuth;
      if (next?.user?.id && state.user?.id && next.user.id !== state.user.id) {
        state.expireSession();
      }
    } catch { /* unparseable value — leave this tab alone */ }
  });
}

export const getToken       = (): string | null => useAuthStore.getState().token;
export const getDivisionId   = (): string | null => useAuthStore.getState().divisionId;
export const getDivisionCode = (): string | null => useAuthStore.getState().divisionCode;
